const AUTH_STORAGE_KEY = 'hospital.session';

export interface AuthUser {
  id: string;
  principalId: string | null;
  accountType: 'staff' | 'patient';
  loginId: string;
  name: string;
  email: string | null;
  roleId: number | null;
  role: string | null;
  canManageAccounts: boolean;
  mustChangePassword: boolean;
}

export interface AuthSession {
  token: string;
  user: AuthUser;
  remembered?: boolean;
}

const isAuthSession = (value: unknown): value is AuthSession => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<AuthSession>;
  return typeof candidate.token === 'string'
    && candidate.token.length > 20
    && typeof candidate.user === 'object'
    && candidate.user !== null
    && typeof candidate.user.id === 'string'
    && typeof candidate.user.name === 'string'
    && (candidate.user.accountType === 'staff' || candidate.user.accountType === 'patient');
};

const readSession = (storage: Storage): AuthSession | null => {
  const raw = storage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isAuthSession(parsed)) return parsed;
  } catch {
    // Invalid or obsolete sessions are removed below.
  }
  storage.removeItem(AUTH_STORAGE_KEY);
  return null;
};

export const getSession = (): AuthSession | null => {
  const localSession = readSession(window.localStorage);
  if (localSession) return localSession;

  const sessionSession = readSession(window.sessionStorage);
  if (sessionSession) {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sessionSession));
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }
  return sessionSession;
};

export const isAuthenticated = (): boolean => getSession() !== null;

export const isRememberedSession = (): boolean => getSession()?.remembered === true;

export const login = (session: AuthSession, remember: boolean): void => {
  logout();
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ ...session, remembered: remember }));
};

export const replaceSessionUser = (user: AuthUser): void => {
  const session = getSession();
  if (!session) return;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ ...session, user }));
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
};

export const replaceSession = (session: AuthSession): void => {
  const remembered = isRememberedSession();
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ ...session, remembered }));
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
};

export const openAuthenticatedRoute = (url: string): Window | null => {
  const opened = window.open('', '_blank');
  if (!opened) return null;

  const session = getSession();
  if (session) {
    opened.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  }
  opened.opener = null;
  opened.location.assign(url);
  return opened;
};

export const logout = (): void => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
};
